import { Component, Input, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { DialerContentService } from '@app/shared/services/dialer-content-service';
import { UserFile } from '@app/shared/user/model/user-file.model';
import { FileUploadModel } from '@app/user-settings/components/voice-greetings/voice-greetings.component';
import { BaseComponentDirective } from '@core/component/base.component';
import { ConfirmationService } from 'primeng/api';
import { Subscription, takeUntil } from 'rxjs';
import { UserService } from '../user.service';

@Component({
    selector: 'app-user-file',
    templateUrl: './user-file.component.html',
    styleUrls: ['./user-file.component.scss'],
})
export class UserFileComponent extends BaseComponentDirective implements OnInit {
    @Input() userId: string;
    @ViewChild('f', { static: true }) ngForm: NgForm;
    @ViewChild('fileInput') fileInput: ElementRef<HTMLInputElement>;
    loading = false;
    userFiles: UserFile[] = [];
    showModal = false;
    selectedRow: FileUploadModel;
    fileName = '';
    selectedFile: File;
    allowValue = true;
    showLoader = false;
    isDragging = false;
    dialerSubscribe: Subscription;

    constructor(private confirmationService: ConfirmationService, private router: Router, private userService: UserService, private dialerContentService: DialerContentService) {
        super();
        this.dialerSubscribe = this.dialerContentService.getUpdate().pipe(takeUntil(this.onDestroy$)).subscribe((message) => {
            this.userId = message.text;
            this.getAllUserFiles();
        });
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.selectedRow = new FileUploadModel();
        this.getAllUserFiles();
    }

    getAllUserFiles() {
        this.loading = true;
        this.userService.getUserFiles(this.userId).pipe(takeUntil(this.onDestroy$)).subscribe({
            next: (res) => {
                this.loading = false;
                this.userFiles = res;
            },
            error: () => {
                this.loading = false;
                this.messageService.add({
                    key: 'tst',
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Something went wrong, please try again',
                });
            },
        });
    }

    cancel() {
        this.showModal = false;
        this.selectedFile = null;
        this.fileName = '';
    }

    triggerUpload(): void {
        this.selectedRow = new FileUploadModel();
        this.showModal = true;
    }

    fileUploadChange(event: Event) {
        const files = (event.target as HTMLInputElement).files;
        if (files.length) {
            this.selectedFile = files[0];
            this.fileName = files[0].name;
            if (!this.selectedRow.name) {
                this.selectedRow.name = files[0].name.replace(/\.[^/.]+$/, '');
            }
        }
    }

    onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = true;
    }

    onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
    }

    onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragging = false;
        const files = event.dataTransfer?.files;
        if (files?.length) {
            this.selectedFile = files[0];
            this.fileName = files[0].name;
            this.selectedRow = new FileUploadModel();
            this.selectedRow.name = files[0].name.replace(/\.[^/.]+$/, '');
            this.showModal = true;
        }
    }

    createAndUpdateFile() {
        const form = new FormData();
        form.append('UploadFile', this.selectedFile);
        form.append('name', this.selectedRow?.name);
        form.append('userId', this.userId);
        form.append('statusId', '1');
        this.showLoader = true;
        this.userService.addUserFile(this.userId, form).pipe(takeUntil(this.onDestroy$)).subscribe({
            next: () => {
                this.showLoader = false;
                this.cancel();
                this.selectedRow = new FileUploadModel();
                this.getAllUserFiles();
                this.messageService.add({
                    key: 'tst',
                    severity: 'success',
                    summary: 'Success',
                    detail: 'File uploaded successfully',
                });
            },
            error: () => {
                this.showLoader = false;
                this.messageService.add({
                    key: 'tst',
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Something went wrong, please try again',
                });
            },
        });
    }

    openFile(userFile: UserFile): void {
        if (userFile.path) {
            window.open(userFile.path, '_blank');
        }
    }

    downloadFile(userFile: UserFile): void {
        if (userFile.path) {
            fetch(userFile.path)
                .then((res) => res.blob())
                .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = userFile.name;
                    a.click();
                    URL.revokeObjectURL(url);
                });
        }
    }

    getFileIcon(name: string): string {
        if (!name) return 'pi pi-file';
        const ext = name.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf': return 'pi pi-file-pdf';
            case 'doc': case 'docx': return 'pi pi-file-word';
            case 'xls': case 'xlsx': case 'csv': return 'pi pi-file-excel';
            case 'png': case 'jpg': case 'jpeg': case 'gif': case 'webp': case 'svg': return 'pi pi-image';
            case 'mp4': case 'mov': case 'avi': case 'webm': return 'pi pi-video';
            case 'mp3': case 'wav': case 'ogg': return 'pi pi-volume-up';
            case 'zip': case 'rar': case '7z': case 'gz': return 'pi pi-box';
            case 'txt': case 'log': return 'pi pi-align-left';
            default: return 'pi pi-file';
        }
    }

    getFileIconClass(name: string): string {
        if (!name) return '';
        const ext = name.split('.').pop()?.toLowerCase();
        switch (ext) {
            case 'pdf': return 'file-icon--pdf';
            case 'doc': case 'docx': return 'file-icon--word';
            case 'xls': case 'xlsx': case 'csv': return 'file-icon--excel';
            case 'png': case 'jpg': case 'jpeg': case 'gif': case 'webp': case 'svg': return 'file-icon--image';
            case 'mp4': case 'mov': case 'avi': case 'webm': return 'file-icon--video';
            default: return 'file-icon--default';
        }
    }

    deleteUserFile(userFile: UserFile) {
        this.userService.deleteUserFile(this.userId, userFile.id).pipe(takeUntil(this.onDestroy$)).subscribe({
            next: () => {
                this.getAllUserFiles();
                this.messageService.add({
                    key: 'tst',
                    severity: 'success',
                    summary: 'Success',
                    detail: `${userFile.name} deleted successfully`,
                });
            },
            error: () => {
                this.messageService.add({
                    key: 'tst',
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Something went wrong, please try again',
                });
            },
        });
    }

    confirmDelete(userFile: UserFile) {
        this.confirmationService.confirm({
            key: 'userFileDelele',
            message: `Are you sure you want to delete "${userFile.name}"?`,
            header: 'Delete File',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.deleteUserFile(userFile);
            },
        });
    }
}
